import { typesenseApiKeyCacheKey } from "@/utils/typesenseApiKeyCacheKey"

/**
 * Re-fetches the scoped Typesense key cached under `cacheKey` after a 401.
 * Client-only: in a browser tab left open past the key's 1h expiry, a refresh is
 * the fix. On the server the key was fetched during the same request, so a 401
 * means the parent key is missing or wrong and a refresh cannot help (see
 * withTypesenseKeyRetry). Concurrent callers share one in-flight refresh.
 */
export function useTypesenseKeyRefresh(cacheKey?: string): { refreshTypesenseApiKey: () => Promise<string> } {
  const { jobShopId } = useHeaderStore()
  const key = cacheKey ?? typesenseApiKeyCacheKey(jobShopId)
  const { data } = useNuxtData<string>(key)

  const refreshing = useState<Promise<string> | null>(`typesenseKeyRefresh-${key}`, () => null)

  async function refreshTypesenseApiKey(): Promise<string> {
    if (refreshing.value)
      return refreshing.value

    refreshing.value = (async () => {
      try {
        await refreshNuxtData(key)
        return data.value ?? ""
      }
      finally {
        refreshing.value = null
      }
    })()

    return refreshing.value
  }

  return { refreshTypesenseApiKey }
}
